/**
 * EventHandlers - tracks and creates bound event handlers for a set of functions.
 */
'use strict';

import Utils from './HlsUtils';

/**
* @access internal
*/
class EventHandlers {

	constructor(config = {}) {
		this._handlers = {};

		this._hlsEventsWired = false;
		this._hlsEvents = null;
		this._hlsObjToBindTo = null;
		this._hlsEmitter = null;

		this._mediaEvents = null;
		this._hlsJsEvents = null;

		Utils.applyConfig(this, config);
	}

	dispose() {
		if( this._hlsEventsWired ) {
			this.unwireHlsEvents();
		}
		this._hlsEvents = null;
		this._hlsObjToBindTo = null;
		this._hlsEmitter = null;

		this._mediaEvents = null;
		this._hlsJsEvents = null;

		this._handlers = null;
	}

	setHlsEventsList( objToBindTo, emitter, eventsList ) {
		EventHandlers.verifyEventsList(eventsList);

		if( this._hlsEvents ) {
			throw new Error( 'HlsEvents already set');
		}

		if( !Utils.isObject( objToBindTo ) ) {
			throw new Error( `objToBindTo must be type: object, ${objToBindTo}`);
		}

		if( !Utils.isObject( emitter ) ) {
			throw new Error( `emitter must be type: object, ${emitter}`);
		}

		if( !Utils.isFunction( emitter.on ) || !Utils.isFunction( emitter.off ) ) {
			throw new Error( `emitter :${emitter} does not appear to be an emitter`);
		}

		this._hlsObjToBindTo = objToBindTo;
		this._hlsEmitter = emitter;
		this._hlsEvents = eventsList;

		return this; // enable chaining
	}

	wireHlsEvent( { event, func } ) {
		let handler = this.createEventHandler( this._hlsObjToBindTo, func );

		if( !handler ) {
			throw new Error( `no handler for event: ${event}, func: ${func}`);
		}

		this._hlsEmitter.on(event, handler);
	}

	unwireHlsEvent( { event, func }) {
		let handler = this.getHandlerForFunction(func);

		if (handler) {
			this._hlsEmitter.off(event, handler);
		}
	}

	wireHlsEvents() {
		if( this._hlsEventsWired ) {
			throw new Error( `hlsEvents are already wired`);
		}

		if( !Utils.isObject(this._hlsObjToBindTo)) {
			throw new Error( `No hlsObjToBindTo: ${this._hlsObjToBindTo}`);
		}

		if( !Utils.isObject(this._hlsEmitter)) {
			throw new Error( `No hlsEmitter: ${this._hlsEmitter}`);
		}

		this._hlsEvents.forEach( event => this.wireHlsEvent( event ));
		this._hlsEventsWired = true;
	}

	unwireHlsEvents() {
		if( !this._hlsEventsWired ) {
			throw new Error( `hlsEvents aren't wired`);
		}

		this._hlsEvents.forEach( event => this.unwireHlsEvent( event ));
		this._hlsEventsWired = false;
	}

	getHandlerKeyForFunction(func) {
		if (!Utils.isFunction(func)) {
			throw new Error('getHandlerForFunction requires a function as argument');
		}

		return 'Handler: ' + func;
	}

	getHandlerForFunction(func) {
		//return this._handlers[this.getHandlerNameForFunction(func)];
		return this._handlers[this.getHandlerKeyForFunction(func)];
	}

	createEventHandler(obj, func) {
	//	let handlerName = this.getHandlerNameForFunction(func);
		let handlerName = this.getHandlerKeyForFunction(func);

		// only do this once when we need to.
		if (!this._handlers[handlerName]) {
			this._handlers[handlerName] = func.bind(obj);
		}

		return this._handlers[handlerName];
	}

	static verifyEventsList(eventsList) {

		// check the eventsList
		if (!eventsList || !eventsList.forEach || !Utils.isFunction(eventsList.forEach)) {
			throw new Error(`verifyEventsList: invalid eventslist`);
		}

		eventsList.forEach(event => {

			// TODO: should also verify the event exists in HlsEvents

			// check the event
			if (!event.event) {
				throw new Error(`verifyEventsList: invalid event: '${event.event}', func: '${event.func}'`);
			}

			// check the function
			if (!Utils.isFunction(event.func)) {
				throw new Error(`verifyEventsList: event: '${event.event}' invalid func: '${event.func}'`);
			}
		});
	}
}

export default EventHandlers;



// WEBPACK FOOTER //
// ./src/EventHandlers.js